import { api } from './client';

export const organizationApi = {
  // === 조직 구조 ===
  getOrganizationTree: async (companyId?: number): Promise<any> => {
    const params: Record<string, any> = {};
    if (companyId !== undefined) params.company_id = companyId;
    const response = await api.get('/api/organization/tree', { params });
    return response.data;
  },

  getOrganizationStats: async (companyId?: number): Promise<any> => {
    const params: Record<string, any> = {};
    if (companyId !== undefined) params.company_id = companyId;
    const response = await api.get('/api/organization/stats', { params });
    return response.data;
  },

  createOrgUnit: async (data: { name: string; code: string; unit_type: string; parent_id?: number; head_employee_id?: string; sort_order?: number }): Promise<any> => {
    const response = await api.post('/api/organization/units', data);
    return response.data;
  },

  createOrganizationUnit: async (data: { name: string; unit_type?: string; company_id?: number; parent_id?: number }): Promise<any> => {
    const response = await api.post('/api/organization/units', data);
    return response.data;
  },

  updateOrgUnit: async (unitId: number, data: { name?: string; head_employee_id?: string; parent_id?: number; sort_order?: number; is_active?: boolean }): Promise<any> => {
    const response = await api.put(`/api/organization/units/${unitId}`, data);
    return response.data;
  },

  moveEmployee: async (data: { employee_id: string; new_org_unit_id: number; reason?: string; effective_date?: string }): Promise<any> => {
    const response = await api.post('/api/organization/move-employee', data);
    return response.data;
  },

  getOrgUnitEmployees: async (unitId: number, includeChildren: boolean = false): Promise<{ unit: { id: number; name: string }; employees: any[]; total: number }> => {
    const response = await api.get(`/api/organization/units/${unitId}/employees`, { params: { include_children: includeChildren } });
    return response.data;
  },

  getOrgUnitDetail: async (unitId: number): Promise<any> => {
    const response = await api.get(`/api/organization/units/${unitId}`);
    return response.data;
  },

  getEmployeeOrgHistory: async (employeeId: string): Promise<any> => {
    const response = await api.get(`/api/organization/employees/${encodeURIComponent(employeeId)}/history`);
    return response.data;
  },

  // === 겸직(복수 조직 소속) ===
  getEmployeeOrgUnits: async (employeeId: string): Promise<any[]> => {
    const response = await api.get(`/api/organization/employees/${encodeURIComponent(employeeId)}/org-units`);
    return response.data?.org_units || [];
  },

  setEmployeeOrgUnits: async (employeeId: string, orgUnits: { org_unit_id: number; is_primary: boolean }[]): Promise<any[]> => {
    const response = await api.put(`/api/organization/employees/${encodeURIComponent(employeeId)}/org-units`, { org_units: orgUnits });
    return response.data?.org_units || [];
  },

  addConcurrentOrg: async (employeeId: string, orgUnitId: number, isPrimary: boolean = false): Promise<any> => {
    const response = await api.post('/api/organization/concurrent', { employee_id: employeeId, org_unit_id: orgUnitId, is_primary: isPrimary });
    return response.data;
  },

  removeConcurrentOrg: async (employeeId: string, orgUnitId: number): Promise<any> => {
    const response = await api.delete(`/api/organization/concurrent/${encodeURIComponent(employeeId)}/${orgUnitId}`);
    return response.data;
  },

  getConcurrentMembers: async (orgUnitId: number): Promise<any[]> => {
    const response = await api.get(`/api/organization/units/${orgUnitId}/concurrent-members`);
    return response.data?.concurrent_members || [];
  },

  // === 회사 관련 ===
  getCompanies: async (): Promise<{ companies: Array<{ id: number; code: string; name: string; parent_company_id: number | null; business_number?: string; representative?: string; address?: string; is_active?: number }> }> => {
    const response = await api.get('/api/companies');
    return response.data;
  },

  getCompanyDetail: async (companyId: number): Promise<any> => {
    const response = await api.get(`/api/companies/${companyId}`);
    return response.data;
  },

  getGroupHierarchy: async (): Promise<{ hierarchy: any[] }> => {
    const response = await api.get('/api/companies/hierarchy');
    return response.data;
  },

  createCompany: async (data: { code: string; name: string; business_number?: string; representative?: string; address?: string; parent_company_id?: number }): Promise<any> => {
    const response = await api.post('/api/companies', data);
    return response.data;
  },

  updateCompany: async (companyId: number, data: { code?: string; name?: string; business_number?: string; representative?: string; address?: string; parent_company_id?: number; is_active?: number }): Promise<any> => {
    const response = await api.put(`/api/companies/${companyId}`, data);
    return response.data;
  },

  getCompanyDomains: async (companyId: number): Promise<{ domains: any[]; total: number }> => {
    const response = await api.get(`/api/companies/${companyId}/domains`);
    return response.data;
  },

  addCompanyDomain: async (companyId: number, data: { email_domain: string; is_default?: boolean }): Promise<any> => {
    const response = await api.post(`/api/companies/${companyId}/domains`, data);
    return response.data;
  },

  addEmailDomain: async (companyId: number, domain: string, isDefault: boolean = false): Promise<any> => {
    const response = await api.post(`/api/companies/${companyId}/domains`, { email_domain: domain, is_default: isDefault });
    return response.data;
  },

  removeCompanyDomain: async (companyId: number, domain: string): Promise<any> => {
    const response = await api.delete(`/api/companies/${companyId}/domains/${encodeURIComponent(domain)}`);
    return response.data;
  },

  // === 조직 효율성 지표 ===
  getOrgEfficiencySummary: async (): Promise<any> => {
    const response = await api.get('/api/org-efficiency/summary');
    return response.data;
  },

  getSpanOfControl: async (): Promise<any> => {
    const response = await api.get('/api/org-efficiency/span-of-control');
    return response.data;
  },

  getRankPyramid: async (): Promise<any> => {
    const response = await api.get('/api/org-efficiency/rank-pyramid');
    return response.data;
  },

  getTeamSizeAnalysis: async (): Promise<any> => {
    const response = await api.get('/api/org-efficiency/team-size');
    return response.data;
  },

  // === AI 분석 접근 범위 (BACK-163) ===
  getAIAccessScope: async (unitId: number): Promise<any> => {
    const response = await api.get(`/api/organization/units/${unitId}/ai-access-scope`);
    return response.data;
  },

  setAIAccessScope: async (unitId: number, scope: string): Promise<any> => {
    const response = await api.put(`/api/organization/units/${unitId}/ai-access-scope`, { scope });
    return response.data;
  },

  // === 겸직/파견 배정 (BACK-126) ===
  getEmployeeAssignments: async (params?: { employee_id?: string; org_unit_id?: number }): Promise<any> => {
    const response = await api.get('/api/organization/assignments', { params: params || {} });
    return response.data;
  },

  createEmployeeAssignment: async (data: any): Promise<any> => {
    const response = await api.post('/api/organization/assignments', data);
    return response.data;
  },

  updateEmployeeAssignment: async (assignmentId: number, data: any): Promise<any> => {
    const response = await api.put(`/api/organization/assignments/${assignmentId}`, data);
    return response.data;
  },

  getCrossEvalWeights: async (employeeId: string): Promise<any> => {
    const response = await api.get(`/api/organization/employees/${encodeURIComponent(employeeId)}/cross-eval-weights`);
    return response.data;
  },

  // === 인사위원회/징계위원회 ===
  getCommittees: async (): Promise<any> => {
    const response = await api.get('/api/committee/');
    return response.data;
  },

  getCommitteeSummary: async (): Promise<any> => {
    const response = await api.get('/api/committee/summary');
    return response.data;
  },

  getCommitteeMembers: async (committeeId: number): Promise<any> => {
    const response = await api.get(`/api/committee/${committeeId}/members`);
    return response.data;
  },

  addCommitteeMember: async (committeeId: number, data: { employee_id: string; role: string; start_date?: string }): Promise<any> => {
    const response = await api.post(`/api/committee/${committeeId}/members`, data);
    return response.data;
  },

  removeCommitteeMember: async (memberId: number): Promise<any> => {
    const response = await api.delete(`/api/committee/members/${memberId}`);
    return response.data;
  },

  getCommitteeAgendas: async (committeeId?: number, status?: string): Promise<any> => {
    const params: any = {};
    if (committeeId) params.committee_id = committeeId;
    if (status) params.status = status;
    const response = await api.get('/api/committee/agendas', { params });
    return response.data;
  },

  createCommitteeAgenda: async (data: { committee_id: number; title: string; agenda_type?: string; target_employee_id?: string; meeting_date?: string; notes?: string }): Promise<any> => {
    const response = await api.post('/api/committee/agendas', data);
    return response.data;
  },

  updateCommitteeAgenda: async (agendaId: number, data: { status?: string; resolution?: string; resolution_type?: string; attendees?: string[]; minutes?: string; notes?: string }): Promise<any> => {
    const response = await api.put(`/api/committee/agendas/${agendaId}`, data);
    return response.data;
  },

  // === 정원 관리 및 채용 ===
  getHeadcountSummary: async (year?: number): Promise<any> => {
    const response = await api.get('/api/headcount/summary', { params: year ? { year } : {} });
    return response.data;
  },

  getHeadcountPlans: async (year?: number): Promise<any> => {
    const response = await api.get('/api/headcount/plans', { params: year ? { year } : {} });
    return response.data;
  },

  setHeadcountPlan: async (data: { department: string; year: number; planned_count: number; notes?: string }): Promise<any> => {
    const response = await api.post('/api/headcount/plans', data);
    return response.data;
  },

  getRecruitments: async (params?: { department?: string; status?: string }): Promise<any> => {
    const response = await api.get('/api/headcount/recruitment', { params: params || {} });
    return response.data;
  },

  createRecruitment: async (data: { department: string; position_title: string; rank?: string; headcount?: number; reason?: string; requester?: string; request_date?: string; target_date?: string; notes?: string }): Promise<any> => {
    const response = await api.post('/api/headcount/recruitment', data);
    return response.data;
  },

  updateRecruitment: async (recordId: number, data: { status?: string; notes?: string; target_date?: string }): Promise<any> => {
    const response = await api.put(`/api/headcount/recruitment/${recordId}`, data);
    return response.data;
  },

  getRecruitmentSummary: async (): Promise<any> => {
    const response = await api.get('/api/headcount/recruitment/summary');
    return response.data;
  },

  getCandidateCounts: async (ids: number[]): Promise<Record<number, number>> => {
    if (ids.length === 0) return {};
    const response = await api.get('/api/headcount/recruitment/candidate-counts', { params: { ids: ids.join(',') } });
    return response.data.counts || {};
  },

  getCandidates: async (headcountId: number): Promise<any[]> => {
    const response = await api.get(`/api/headcount/recruitment/${headcountId}/candidates`);
    return response.data.candidates || [];
  },

  createCandidate: async (headcountId: number, data: {
    name: string; email?: string; phone?: string; source?: string;
    current_stage?: string; interviewer?: string; notes?: string;
    resume_url?: string; applied_at?: string;
  }): Promise<any> => {
    const response = await api.post(`/api/headcount/recruitment/${headcountId}/candidates`, data);
    return response.data;
  },

  updateCandidate: async (headcountId: number, candidateId: number, data: {
    current_stage?: string; interviewer?: string; notes?: string; resume_url?: string;
  }): Promise<any> => {
    const response = await api.put(`/api/headcount/recruitment/${headcountId}/candidates/${candidateId}`, data);
    return response.data;
  },

  deleteCandidate: async (headcountId: number, candidateId: number): Promise<any> => {
    const response = await api.delete(`/api/headcount/recruitment/${headcountId}/candidates/${candidateId}`);
    return response.data;
  },
};
